import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    Alert,
    CircularProgress,
    Stepper,
    Step,
    StepLabel
} from '@mui/material';
import {
    AccountBalance as BankIcon,
    CheckCircle as SuccessIcon
} from '@mui/icons-material';
import { usePlaidLink } from 'react-plaid-link';
import { useMutation, useQuery } from 'react-query';
import { toast } from 'sonner';
import API from '../../../../../API';

/**
 * Bank Account Linking Form using Plaid Link
 */
function BankAccountForm({ company, onSuccess, onCancel }) {
    const [linkToken, setLinkToken] = useState(null);
    const [isLinking, setIsLinking] = useState(false);

    // Fetch Plaid Link token when component mounts
    useEffect(() => {
        if (company) {
            fetchLinkToken();
        }
    }, [company]);

    const fetchLinkToken = async () => {
        try {
            const response = await API.admin.createCompanyBankLinkToken({ company_id: company.id });
            setLinkToken(response.link_token);
        } catch (error) {
            toast.error(`Failed to initialize: ${error.message}`);
        }
    };

    // Link bank account mutation
    const linkAccountMutation = useMutation(
        (data) => API.admin.createCompanyBankAccount(data),
        {
            onSuccess: (response) => {
                setIsLinking(false);
                if (response.dwolla_onboarded) {
                    toast.success('Bank account linked and Dwolla connected!');
                } else if (response.dwolla_error) {
                    toast.success('Bank account linked. Dwolla connection failed - use manual button.');
                } else {
                    toast.success('Bank account linked successfully!');
                }
                onSuccess();
            },
            onError: (error) => {
                setIsLinking(false);
                toast.error(`Failed to link account: ${error.message}`);
            }
        }
    );

    // Configure Plaid Link
    const { open, ready } = usePlaidLink({
        token: linkToken,
        onSuccess: (public_token, metadata) => {
            setIsLinking(true);
            // Link the account via backend
            linkAccountMutation.mutate({
                company_id: company.id,
                public_token: public_token,
                account_id: metadata.account_id,
                metadata: {
                    institution: metadata.institution,
                    accounts: metadata.accounts
                }
            });
        },
        onExit: (err, metadata) => {
            if (err != null) {
                toast.error(`Linking cancelled: ${err.error_message || 'Unknown error'}`);
            }
        },
    });

    return (
        <Box>
            <Alert severity="info" sx={{ mb: 3 }}>
                <Typography variant="body2">
                    <strong>Company:</strong> {company?.name || 'Unknown'}
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                    This bank account will be used for automated ACH reimbursements
                    after creator payouts. Internal use only.
                </Typography>
            </Alert>

            {!linkToken ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress />
                    <Typography variant="body2" sx={{ ml: 2 }}>
                        Initializing bank account linking...
                    </Typography>
                </Box>
            ) : (
                <>
                    <Box sx={{ mb: 3, textAlign: 'center', py: 4 }}>
                        <BankIcon sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
                        <Typography variant="h6" gutterBottom>
                            Ready to Link Bank Account
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                            Click the button below to securely connect your company's bank account through Plaid.
                        </Typography>
                        <Button
                            variant="contained"
                            size="large"
                            onClick={() => open()}
                            disabled={!ready || isLinking}
                            startIcon={isLinking ? <CircularProgress size={20} /> : <BankIcon />}
                        >
                            {isLinking ? 'Linking Account...' : 'Connect Bank Account'}
                        </Button>
                    </Box>

                    <Alert severity="warning" sx={{ mb: 2 }}>
                        <Typography variant="caption">
                            By connecting your bank account, you authorize Blitz to debit the specified
                            bank account for ACH reimbursements related to creator payouts.
                        </Typography>
                    </Alert>
                </>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
                <Button
                    onClick={onCancel}
                    disabled={isLinking}
                >
                    Cancel
                </Button>
            </Box>
        </Box>
    );
}

/**
 * Bank Account Linking Dialog
 *
 * Modal dialog with Plaid Link integration for linking company bank accounts.
 * Uses Plaid for secure bank account collection and ACH verification.
 */
function BankAccountLinkingDialog({ open, onClose, company, onSuccess }) {
    const [step, setStep] = useState(0);

    const steps = ['Select Company', 'Connect Bank Account', 'Success'];

    useEffect(() => {
        if (open && company) {
            setStep(1); // Move to bank details step when dialog opens
        } else if (!open) {
            // Reset to step 0 when dialog closes
            setTimeout(() => setStep(0), 300);
        }
    }, [open, company]);

    const handleClose = () => {
        setStep(0);
        onClose();
    };

    const handleSuccess = () => {
        setStep(2); // Move to success step
        setTimeout(() => {
            setStep(0);
            onSuccess();
        }, 2000);
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
            disableEscapeKeyDown={step === 1} // Prevent closing during linking
        >
            <DialogTitle>
                <BankIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Link Company Bank Account
            </DialogTitle>

            <DialogContent>
                <Stepper activeStep={step} sx={{ mb: 3 }}>
                    {steps.map((label) => (
                        <Step key={label}>
                            <StepLabel>{label}</StepLabel>
                        </Step>
                    ))}
                </Stepper>

                {step === 1 && company && (
                    <BankAccountForm
                        company={company}
                        onSuccess={handleSuccess}
                        onCancel={handleClose}
                    />
                )}

                {step === 2 && (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                        <SuccessIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
                        <Typography variant="h6" gutterBottom>
                            Bank Account Linked Successfully!
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            The bank account has been securely linked through Plaid and is ready for ACH transfers.
                        </Typography>
                    </Box>
                )}
            </DialogContent>

            {step === 2 && (
                <DialogActions>
                    <Button onClick={handleClose} variant="contained">
                        Close
                    </Button>
                </DialogActions>
            )}
        </Dialog>
    );
}

export default BankAccountLinkingDialog;
